import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import {
  MasterTemplateDTO,
  VisitNoteBillDTO,
  VisitNoteDTO,
} from 'dtos';
import {
  VisitNote as VisitNoteType,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  normalizeBillingCodes,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  useDeleteTemplate,
  useSoapNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  settingsEventChannel,
  visitNoteEventChannel,
  visitNoteFormEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import MainContent, {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import VisitNoteFooter from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteFooter';
import NewMasterTemplateDialog from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/NewMasterTemplateDialog';
import useSendToReferringProvider from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/useSendToReferringProvider';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  VisitNoteFormBridgeContext,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/contexts/visitNoteFormBridge';
import {
  VisitNoteBillingCode,
} from 'views/EMR/components/ServiceLookup/type';
import BulkSectionTemplateDialog from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/BulkSectionTemplateDialog';

export type FormCommands = {
  applyMasterTemplate: (template: MasterTemplateDTO) => void;
  openBulkSectionTemplates: () => void;
  prepareForExternalUpdate: () => Promise<boolean>;
};

type UsePrepareForExternalUpdateParams = {
  isFinalized: boolean;
  onSilentSave: (data: VisitNoteData) => Promise<unknown>;
};

export function usePrepareForExternalUpdate({
  isFinalized,
  onSilentSave,
}: UsePrepareForExternalUpdateParams): () => Promise<boolean> {
  const notify = useNotify();
  const {
    getValues,
    trigger,
    reset,
    formState: {
      isDirty,
    },
  } = useFormContext<VisitNoteData>();

  return useCallback(async (): Promise<boolean> => {
    if (isFinalized || !isDirty) {
      return true;
    }

    const isFormValid = await trigger();
    if (!isFormValid) {
      notify({
        variant: 'error',
        message: 'Fix visit note errors before continuing',
      });
      return false;
    }

    try {
      const formData = getValues();
      const saved = await onSilentSave(formData);
      if (isNil(saved)) {
        return false;
      }
      reset(formData, {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
      });
      return true;
    } catch {
      return false;
    }
  }, [getValues, isDirty, isFinalized, notify, onSilentSave, reset, trigger]);
}

type Props = {
  visitNote: VisitNoteType;
  editable: boolean;
  savingVisitNote: boolean;
  unfinalizingVisitNote: boolean;
  isPrinting: boolean;
  onCommandsChange?: (commands: FormCommands) => void;
  onSave: (data: VisitNoteData) => Promise<void>;
  onSilentSave: (data: VisitNoteData) => Promise<unknown>;
  onFinalize: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onUnfinalize: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onUpdateBillingState: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onPrint?: (data: VisitNoteData) => Promise<void>;
};

function VisitNoteFormOutletContent({
  visitNote,
  editable,
  savingVisitNote,
  unfinalizingVisitNote,
  isPrinting,
  onCommandsChange,
  onSave,
  onSilentSave,
  onFinalize,
  onUnfinalize,
  onUpdateBillingState,
  onPrint,
}: Props) {
  const {
    control,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<VisitNoteData>();
  const serviceCodes = useWatch({
    control,
    name: 'bill.serviceCodes',
  }) ?? [];
  const {
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'bill.serviceCodes',
  });
  const { syncFromForm, needsRefresh } = useContext(VisitNoteFormBridgeContext);
  const { mutateAsync: deleteTemplate } = useDeleteTemplate();
  const { handleSoapNote } = useSoapNote();
  const { appendToTextFields, appendToBillFields } = useAppendToVisitNote();
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);
  const [showBulkSectionTemplates, setShowBulkSectionTemplates] = useState<boolean>(false);
  const isFinalized = !!visitNote.isFinalized;
  const prepareForExternalUpdate = usePrepareForExternalUpdate({
    isFinalized,
    onSilentSave,
  });
  const {
    sendToReferringProviderDisabledReason,
    isSendToReferringProviderDisabled,
    handleSendToReferringProvider,
  } = useSendToReferringProvider({
    encounterId: visitNote.encounterId,
    isFinalized,
    finalizedVisitNoteMultiMediaId: visitNote.finalizedVisitNoteMultiMediaId,
    isNoteActionDisabled: needsRefresh || isSubmitting || savingVisitNote || unfinalizingVisitNote,
  });

  const handleOpenFormWorkflow = useCallback(async (category: FormCategory) => {
    const canOpen = await prepareForExternalUpdate();
    if (!canOpen) {
      return;
    }
    visitNoteFormEventChannel.emit('onOpenStartFormWorkflow', {
      patient: visitNote.patient as PatientCompact,
      encounterId: visitNote.encounterId,
      lockEncounter: true,
      defaultCategory: category,
    });
  }, [prepareForExternalUpdate, visitNote.encounterId, visitNote.patient]);

  useEffect(() => {
    syncFromForm(serviceCodes as VisitNoteBillingCode[], append, remove);
  }, [append, remove, serviceCodes, syncFromForm]);

  const handleApplyMasterTemplate = useCallback((template: MasterTemplateDTO) => {
    const bill = {
      assessmentCodes: template?.assessmentCodes ?? [],
      serviceCodes: normalizeBillingCodes(
        template?.billingCodes ?? [],
        {},
        template?.assessmentCodes ?? [],
      ),
      customServiceCodes: (template?.customServiceCodes ?? []).map(
        ({ keyId, ...item }) => item,
      ),
    };

    appendToTextFields(template);
    appendToBillFields({
      ...bill,
    } as VisitNoteBillDTO);
  }, [appendToBillFields, appendToTextFields]);

  const prepareRef = useRef(prepareForExternalUpdate);
  prepareRef.current = prepareForExternalUpdate;

  useEffect(() => {
    onCommandsChange?.({
      applyMasterTemplate: handleApplyMasterTemplate,
      openBulkSectionTemplates: () => { setShowBulkSectionTemplates(true); },
      prepareForExternalUpdate: () => prepareRef.current(),
    });
  }, [handleApplyMasterTemplate, onCommandsChange]);

  useEffect(() => {
    if (!isNil(visitNote)) {
      reset(visitNote, {
        keepDirty: false,
        keepTouched: false,
      });
      handleSoapNote();
    }
  }, [handleSoapNote, reset, visitNote]);

  useEffect(() => {
    const unsubscribeMIPS = visitNoteEventChannel.on('onMIPSUpdate', (mipsList) => {
      setValue('bill.relatedMipsList', mipsList);
    });

    const unsubscribeTemplateDelete = visitNoteEventChannel.on('onTemplateDelete', async (template) => {
      await deleteTemplate(template);
      settingsEventChannel.emit('onVisitNoteSettingsChange');
    });

    const unsubscribeFromSaveAsMasterTemplate = visitNoteEventChannel.on('onSaveAsMasterTemplate', () => {
      setShowTemplateDialog(true);
    });

    return () => {
      unsubscribeMIPS();
      unsubscribeTemplateDelete();
      unsubscribeFromSaveAsMasterTemplate();
    };
  }, [deleteTemplate, setValue]);

  return (
    <>
      <MainContent
        encounterId={visitNote.encounterId}
        visitNote={visitNote}
        editable={editable}
        savingVisitNote={savingVisitNote}
        onSubmit={onSave}
      />
      <VisitNoteFooter
        isFinalized={isFinalized}
        sendDocumentsActions={{
          onSendOrderForm: () => { handleOpenFormWorkflow(FormCategory.OrderForm); },
          onSendReferral: () => { handleOpenFormWorkflow(FormCategory.ReferralOutgoing); },
          onSendToReferringProvider: handleSendToReferringProvider,
          referringProviderDisabledReason: sendToReferringProviderDisabledReason,
          isSendToReferringProviderDisabled,
        }}
        savingVisitNote={savingVisitNote}
        unfinalizingVisitNote={unfinalizingVisitNote}
        isPrinting={isPrinting}
        isFinalizedPdfPending={visitNote.isFinalized === true
          && isNil(visitNote.finalizedVisitNoteMultiMediaId)}
        onSave={onSave}
        onFinalize={onFinalize}
        onUnfinalize={onUnfinalize}
        onUpdateBillingState={onUpdateBillingState}
        onPrint={onPrint}
      />
      {showTemplateDialog && (
        <NewMasterTemplateDialog onClose={() => { setShowTemplateDialog(false); }} />
      )}
      {showBulkSectionTemplates && (
        <BulkSectionTemplateDialog
          onClose={() => { setShowBulkSectionTemplates(false); }}
        />
      )}
    </>
  );
}

export default function VisitNoteFormOutlet(props: Props) {
  const formProps = useForm<VisitNoteData>();

  return (
    <FormProvider {...formProps}>
      <VisitNoteFormOutletContent {...props} />
    </FormProvider>
  );
}
